import { useEffect, useState, useCallback } from 'react';
import { getContext } from '@fiverr-private/fiverr_context';
import { marketplaceIntegration } from '@fiverr-private/logo_maker_analytics/dist/esm/mixpanel';
import { listings as listingsApi } from '@fiverr-private/variations_display/src/api';
import { getTotalListingPages } from '../../../../../utils/getTotalListingPages';
import useIntersectionObserver from '../../../../../hooks/useIntersectionObserver';
import { scrollToTopOfPage } from '../../../shared/utils';
import { serializeBriefData } from './utils';

const { store: mixpanelStore, reportListingView } = marketplaceIntegration;

const loadPage = (page, briefId, briefData, totalPages) =>
    listingsApi.get({
        briefId,
        briefData: serializeBriefData(briefData),
        page,
        skipTest: true,
        totalPages,
    });

const updateStatisticsAndReportListing = (statistics) => {
    mixpanelStore.statistics.setData(statistics);
    reportListingView();
};

export const useListingUpdate = ({
    initialProjects,
    briefId,
    staticFiltersRef,
    briefData,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [projects, setProjects] = useState(initialProjects || []);
    const [page, setPage] = useState(0);
    const [stickyFiltersVisible, setStickyFiltersVisible] = useState(false);
    const [hasNextPage, setHasNextPage] = useState(true);
    const [totalPages] = useState(() => {
        const { isMobile, isTouch } = getContext();

        return getTotalListingPages({ isMobile, isTouch });
    });

    useIntersectionObserver([staticFiltersRef?.current], ([entry]) => {
        setStickyFiltersVisible(!entry.isIntersecting);
    });

    const scrollToTheTopOfListing = () => {
        if (!stickyFiltersVisible) {
            return;
        }
        scrollToTopOfPage();
    };

    const resetListing = async () => {
        setIsLoading(true);

        const {
            projects: newProjects,
            hasNextPage: nextPageExists,
            statistics,
        } = await loadPage(0, briefId, briefData, totalPages);

        if (newProjects) {
            scrollToTheTopOfListing();
            setProjects(newProjects);
            setPage(1);
            setHasNextPage(nextPageExists);

            updateStatisticsAndReportListing(statistics);
        }
        setIsLoading(false);
    };

    const loadMore = useCallback(async () => {
        const { projects: newProjects, hasNextPage: nextPageExists } =
            await loadPage(page, briefId, briefData, totalPages);

        if (newProjects) {
            setProjects((oldProjects) =>
                page === 0 ? newProjects : [...oldProjects, ...newProjects]
            );
            setPage((prevPage) => prevPage + 1);
            setHasNextPage(nextPageExists);
        }
    }, [page, briefId, briefData, totalPages]);

    useEffect(() => {
        resetListing();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [briefData.keywords, briefData.industries]);

    return {
        projects,
        isLoading,
        stickyFiltersVisible,
        hasNextPage,
        loadMore,
        briefId,
        page,
    };
};
